import { ReactNode, useCallback, useEffect, useState } from "react";
import { useTechpackFetcher } from "../../../utils/useTechpackFetcher";
import { useDesignFetcher } from "../../../utils/DesignsFetcherHooks";
import { Techpack, TechpackItem, TechpackItemArtwork } from "../../../types";
import { useParams } from "react-router-dom";
import ImageService from "../../../service/ImageService";
import { AdminTechpackContext } from "./AdminTechpackContext";

const AdminTechpackProvider = ({ children }: { children: ReactNode }) => {
  const { fetchPreOrderTechpack } = useTechpackFetcher();
  const { fetchDecorationsOptionsDefinition } = useDesignFetcher();
  const [techpackDetail, setTechpackDetail] = useState<Techpack | null>(null);
  const [isTeckpackLoaded, setIsTeckpackLoaded] = useState(false);
  const { orderId } = useParams();

  const [needTechpackRefresh, setNeedTechpackRefresh] = useState(true);

  const updateTechpackItemArtwork = (
    techpackItemId: string,
    techpackItemArtwork: TechpackItemArtwork
  ) => {
    setTechpackDetail((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        techpackitems: prev.techpackitems.map((item) =>
          item.id === techpackItemId
            ? {
                ...item,
                techpackitemartworks: item.techpackitemartworks.map(
                  (artwork) =>
                    artwork.id === techpackItemArtwork.id
                      ? techpackItemArtwork
                      : artwork
                ),
              }
            : item
        ),
      };
    });
  };

  const updateTechpack = (techpack: Techpack) => {
    setTechpackDetail(techpack);
  };

  const updateTechpackItem = (techpackItem: TechpackItem) => {
    setTechpackDetail((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        techpackitems: prev.techpackitems.map((item) =>
          item.id === techpackItem.id ? techpackItem : item
        ),
      };
    });
  };

  useEffect(() => {
    if (!needTechpackRefresh) {
      return;
    }

    //TODO: We should do this in the backend or have a specific service to keep the fabric info!
    fetchPreOrderTechpack(orderId || "")
      .then(async (techpack: Techpack) => {
        if (techpack.techpackitems) {
          const definitions = await fetchDecorationsOptionsDefinition();

          await Promise.all(
            techpack.techpackitems.map(async (techpackItem) => {
              techpackItem.decorationDefinitions =
                definitions.decorationDefinitions;

              await Promise.all(
                techpackItem.techpackitemartworks.map(
                  async (techpackItemArtwork) => {
                    const decoration = techpackItemArtwork.decoration;
                    if (
                      decoration.assetId &&
                      decoration.active &&
                      !decoration.inError &&
                      decoration.asset?.useColorExtractionAndMatching
                    ) {
                      decoration.fabricColorCodes =
                        await ImageService.getColorCodes(decoration.type);
                    }
                  }
                )
              );
            })
          );
        }
        return techpack;
      })
      .then((techpack) => {
        setTechpackDetail(techpack);
        setIsTeckpackLoaded(true);
        setNeedTechpackRefresh(false);
      });
  }, [
    orderId,
    fetchPreOrderTechpack,
    fetchDecorationsOptionsDefinition,
    needTechpackRefresh,
  ]);

  const refetchTechpack = useCallback(() => {
    setNeedTechpackRefresh(true);
  }, [setNeedTechpackRefresh]);

  return (
    <AdminTechpackContext.Provider
      value={{
        teckpackDetails: techpackDetail,
        updateTechpackItemArtwork,
        updateTechpack,
        updateTechpackItem,
        isTeckpackLoaded,
        refetchTechpack,
      }}
    >
      {children}
    </AdminTechpackContext.Provider>
  );
};

export default AdminTechpackProvider;
